/* ==========================================================================
   MSP Supply — Pumps, Filters & Heaters — DOM wiring / render layer.
   Loaded after pfh-calc.js (which defines window.MSPPFH).
   ========================================================================== */
(function () {
  "use strict";
  var M = window.MSPPFH;
  if (!M) return;
  var state = M.state, $ = M.$, $all = M.$all, fmt = M.fmt, clamp = M.clamp, num = M.num;

  var gallonsManuallyEditedInSection1 = false;

  /* ------------------------------------------------------------------ *
   * Segmented / option button groups — generic wiring
   * ------------------------------------------------------------------ */
  // dataAttr lets the HTML use a shorter/different data-* name than the
  // state key it drives (e.g. data-tdh -> state.tdhLevel). Defaults to
  // stateKey when the names already match (e.g. data-shape -> state.shape).
  // Values are always stored as-is (strings) — these are categorical
  // selections, never numbers, so no parseFloat coercion here.
  function wireSegmented(selectorAttr, stateKey, dataAttr, onChange) {
    if (typeof dataAttr === "function") { onChange = dataAttr; dataAttr = null; }
    var attrName = "data-" + (dataAttr || stateKey);
    $all("[" + selectorAttr + "]").forEach(function (group) {
      $all(".pfh-seg-btn, .pfh-opt", group).forEach(function (btn) {
        btn.addEventListener("click", function () {
          var val = btn.getAttribute(attrName);
          state[stateKey] = val;
          if (onChange) onChange(val);
          renderAll();
        });
      });
    });
  }

  function syncGroupUI(selectorAttr, stateKey, dataAttr) {
    var attrName = "data-" + (dataAttr || stateKey);
    $all("[" + selectorAttr + "]").forEach(function (group) {
      $all(".pfh-seg-btn, .pfh-opt", group).forEach(function (btn) {
        var val = btn.getAttribute(attrName);
        var pressed = String(state[stateKey]) === String(val);
        btn.setAttribute("aria-pressed", pressed ? "true" : "false");
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Pool shape dimension panels
   * ------------------------------------------------------------------ */
  function wireShapeInputs() {
    var map = {
      "pfh-rect-length": ["rectangle", "length"],
      "pfh-rect-width": ["rectangle", "width"],
      "pfh-rect-shallow": ["rectangle", "shallow"],
      "pfh-rect-deep": ["rectangle", "deep"],
      "pfh-round-diameter": ["round", "diameter"],
      "pfh-round-shallow": ["round", "shallow"],
      "pfh-round-deep": ["round", "deep"],
      "pfh-oval-longd": ["oval", "longD"],
      "pfh-oval-shortd": ["oval", "shortD"],
      "pfh-oval-shallow": ["oval", "shallow"],
      "pfh-oval-deep": ["oval", "deep"]
    };
    Object.keys(map).forEach(function (id) {
      var el = $("#" + id);
      if (!el) return;
      el.addEventListener("input", function () {
        var shape = map[id][0], field = map[id][1];
        state.dims[shape][field] = num(el.value, 0);
        // typing a real measurement re-links gallons to the calculator
        if (state.gallonsSource === "manual") {
          // leave manual mode alone unless the user explicitly resets it
        }
        renderAll();
      });
    });
  }

  function populateShapeInputs() {
    var d = state.dims;
    var setVal = function (id, v) {
      var el = $("#" + id);
      if (el && document.activeElement !== el) el.value = v;
    };
    setVal("pfh-rect-length", d.rectangle.length);
    setVal("pfh-rect-width", d.rectangle.width);
    setVal("pfh-rect-shallow", d.rectangle.shallow);
    setVal("pfh-rect-deep", d.rectangle.deep);
    setVal("pfh-round-diameter", d.round.diameter);
    setVal("pfh-round-shallow", d.round.shallow);
    setVal("pfh-round-deep", d.round.deep);
    setVal("pfh-oval-longd", d.oval.longD);
    setVal("pfh-oval-shortd", d.oval.shortD);
    setVal("pfh-oval-shallow", d.oval.shallow);
    setVal("pfh-oval-deep", d.oval.deep);
  }

  function updateShapePanels() {
    $all("[data-dims-panel]").forEach(function (panel) {
      var isRect = panel.id === "pfh-dims-" + state.shape;
      panel.hidden = !isRect;
    });
  }

  /* ------------------------------------------------------------------ *
   * Manual gallons override (Section 2 banner)
   * ------------------------------------------------------------------ */
  function wireManualGallons() {
    var toggleBtn = $("#pfh-s-manual-toggle");
    var row = $("#pfh-s-manual-row");
    var input = $("#pfh-s-manual-input");
    var cancelBtn = $("#pfh-s-manual-cancel");
    if (!toggleBtn || !row || !input || !cancelBtn) return; // Equipment Sizing section not on this page
    toggleBtn.addEventListener("click", function () {
      row.hidden = false;
      toggleBtn.parentElement.hidden = true;
      input.value = state.manualGallons != null ? state.manualGallons : M.calcGallons();
      input.focus();
    });
    cancelBtn.addEventListener("click", function () {
      state.gallonsSource = "calculated";
      state.manualGallons = null;
      row.hidden = true;
      toggleBtn.parentElement.hidden = false;
      renderAll();
    });
    input.addEventListener("input", function () {
      state.gallonsSource = "manual";
      state.manualGallons = num(input.value, 0);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * Section 1 — gallons field (auto-syncs unless user overrides)
   * ------------------------------------------------------------------ */
  function wireRuntimeGallons() {
    var input = $("#pfh-r-gallons");
    var editBtn = $("#pfh-r-gallons-edit");
    if (!input) return; // Pump Runtime section not on this page
    input.addEventListener("input", function () {
      gallonsManuallyEditedInSection1 = true;
      state.gallonsSource = "manual";
      state.manualGallons = num(input.value, 0);
      renderAll();
    });
    if (editBtn) editBtn.addEventListener("click", function () {
      input.focus();
      input.select();
    });
  }

  /* ------------------------------------------------------------------ *
   * Speed slider <-> RPM number input (bidirectional)
   * ------------------------------------------------------------------ */
  function wireSpeed() {
    var slider = $("#pfh-speed-slider");
    var rpmInput = $("#pfh-rpm-input");
    if (!slider || !rpmInput) return; // Pump Runtime section not on this page
    slider.addEventListener("input", function () {
      state.speedPercent = clamp(parseFloat(slider.value), 20, 100);
      renderAll();
    });
    rpmInput.addEventListener("input", function () {
      var rpm = clamp(num(rpmInput.value, M.MAX_RPM * 0.6), M.MAX_RPM * 0.2, M.MAX_RPM);
      state.speedPercent = clamp(Math.round((rpm / M.MAX_RPM) * 100), 20, 100);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * TDH advanced toggle
   * ------------------------------------------------------------------ */
  function wireTdhAdvanced() {
    var toggle = $("#pfh-tdh-advanced-toggle");
    var row = $("#pfh-tdh-advanced-row");
    var input = $("#pfh-tdh-advanced-input");
    if (!toggle || !row || !input) return; // Equipment Sizing section not on this page
    toggle.addEventListener("click", function () {
      row.hidden = !row.hidden;
      if (!row.hidden) {
        state.tdhLevel = "advanced";
        input.value = state.tdhAdvancedFt;
        input.focus();
        renderAll();
      }
    });
    input.addEventListener("input", function () {
      state.tdhLevel = "advanced";
      state.tdhAdvancedFt = num(input.value, 55);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * Plumbing advanced input
   * ------------------------------------------------------------------ */
  function wirePipeAdvanced() {
    var input = $("#pfh-pipe-advanced-input");
    if (!input) return; // Equipment Sizing section not on this page
    input.addEventListener("input", function () {
      state.pipeAdvancedGpm = num(input.value, 73);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * Equipment tabs (Pump / Filter / Heater)
   * ------------------------------------------------------------------ */
  function wireTabs() {
    $all(".pfh-tab").forEach(function (tab) {
      tab.addEventListener("click", function () {
        state.equipTab = tab.getAttribute("data-tab");
        renderTabs();
      });
    });
  }
  function renderTabs() {
    $all(".pfh-tab").forEach(function (tab) {
      var active = tab.getAttribute("data-tab") === state.equipTab;
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
    ["pump", "filter", "heater"].forEach(function (name) {
      var panel = $("#pfh-panel-" + name);
      if (!panel) return;
      var active = name === state.equipTab;
      panel.hidden = !active;
      panel.classList.toggle("active", active);
    });
  }

  /* ------------------------------------------------------------------ *
   * Heater inputs
   * ------------------------------------------------------------------ */
  function wireHeaterInputs() {
    var hCurrent = $("#pfh-h-current"), hTarget = $("#pfh-h-target"), hAir = $("#pfh-h-air");
    if (!hCurrent || !hTarget || !hAir) return; // Heater section not on this page
    hCurrent.addEventListener("input", function (e) {
      state.currentTemp = num(e.target.value, 0);
      renderAll();
    });
    hTarget.addEventListener("input", function (e) {
      state.targetTemp = num(e.target.value, 0);
      renderAll();
    });
    hAir.addEventListener("input", function (e) {
      state.airTemp = num(e.target.value, 80);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * Heater standalone fallback — pool surface area + gallons, used only
   * when the Equipment Sizing section isn't present on the same page
   * (see M.sizingSectionPresent() in pfh-calc.js).
   * ------------------------------------------------------------------ */
  function wireHeaterManualInputs() {
    var areaInput = $("#pfh-h-manual-area");
    var gallonsInput = $("#pfh-h-manual-gallons");
    if (!areaInput || !gallonsInput) return; // Heater section not on this page
    areaInput.addEventListener("input", function () {
      state.heaterManualArea = num(areaInput.value, 0);
      renderAll();
    });
    gallonsInput.addEventListener("input", function () {
      state.heaterManualGallons = num(gallonsInput.value, 0);
      renderAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * RENDER — recompute everything and paint the DOM
   * ------------------------------------------------------------------ */
  function renderAll() {
    // Each of the three tools is now its own independent Shopify section
    // (sections/pfh-pump-runtime.liquid, pfh-equipment-sizing.liquid,
    // pfh-heater-calculator.liquid). Any subset of them can be on a given
    // page, so every step below is gated on the relevant section actually
    // being present rather than assumed.
    var hasRuntime = !!$("#pfh-runtime");
    var hasSizing = !!$("#pfh-sizing");
    var hasHeater = !!$("#pfh-heater");

    if (hasSizing) {
      updateShapePanels();
      populateShapeInputs();
      syncGroupUI("data-shape-group", "shape");
      syncGroupUI("data-tdh-group", "tdhLevel", "tdh");
      syncGroupUI("data-pipe-group", "pipeSize", "pipe");
      var tdhAdvRow = $("#pfh-tdh-advanced-row");
      if (tdhAdvRow) tdhAdvRow.hidden = state.tdhLevel !== "advanced";
      var pipeAdvRow = $("#pfh-pipe-advanced-row");
      if (pipeAdvRow) pipeAdvRow.hidden = state.pipeSize !== "advanced";
    }
    if (hasHeater) {
      syncGroupUI("data-heatertype-group", "heaterType", "heatertype");
    }

    // Turnover-goal segmented groups live in both Section 1 and Section 2;
    // this naturally covers whichever of the two (or both, or neither) is
    // on the page.
    $all("[data-turnover-group]").forEach(function (group) {
      $all(".pfh-seg-btn", group).forEach(function (btn) {
        btn.setAttribute("aria-pressed", String(state.turnoverHours) === btn.getAttribute("data-turnover") ? "true" : "false");
      });
    });

    if (hasRuntime) renderRuntimeSection();
    if (hasSizing) renderSizingSection();
    if (hasHeater) renderHeaterSection();
  }

  /* ---- Section 1 : Pump Runtime Calculator ---- */
  function renderRuntimeSection() {
    var gallons = M.activeGallons();
    var rGallonsInput = $("#pfh-r-gallons");
    if (document.activeElement !== rGallonsInput) rGallonsInput.value = gallons;
    $("#pfh-r-gallons-hint").innerHTML = state.gallonsSource === "manual"
      ? 'Using your entered volume. <button type="button" class="pfh-manual-toggle" id="pfh-r-gallons-reset">Use calculated volume</button>'
      : 'Using the pool volume calculated below. <button type="button" class="pfh-manual-toggle" id="pfh-r-gallons-edit2">Enter my own</button>';
    var resetBtn = $("#pfh-r-gallons-reset");
    if (resetBtn) resetBtn.addEventListener("click", function () {
      state.gallonsSource = "calculated";
      state.manualGallons = null;
      renderAll();
    });
    var editBtn2 = $("#pfh-r-gallons-edit2");
    if (editBtn2) editBtn2.addEventListener("click", function () { rGallonsInput.focus(); rGallonsInput.select(); });

    var slider = $("#pfh-speed-slider");
    slider.value = state.speedPercent;
    slider.style.setProperty("--fill", ((state.speedPercent - 20) / 80) * 100 + "%");
    var rpmInput = $("#pfh-rpm-input");
    if (document.activeElement !== rpmInput) rpmInput.value = Math.round((state.speedPercent / 100) * M.MAX_RPM);

    var rt = M.calcRuntime();
    $("#pfh-r-runhours").textContent = fmt(rt.recommendedRunHours, 1);
    $("#pfh-r-flow").textContent = fmt(rt.estFlowGPM, 0);
    $("#pfh-r-turnover").textContent = fmt(rt.turnoverHoursAtSpeed, 1);
    $("#pfh-r-rpm").textContent = fmt(rt.rpm, 0);
    $("#pfh-r-speedpct").textContent = fmt(state.speedPercent, 0) + "%";
  }

  /* ---- Section 2 : Equipment Sizing Calculator ---- */
  function renderSizingSection() {
    var gallons = M.activeGallons();
    $("#pfh-s-gallons").textContent = fmt(gallons, 0);
    var flow = M.calcRequiredFlow();
    $("#pfh-s-gpm").textContent = fmt(flow.gpm, 0);

    var tier = M.poolSizeTier(gallons);
    var pumpGuide = M.PUMP_GUIDE[tier];
    $("#pfh-pump-hp").textContent = pumpGuide.pump;
    $("#pfh-pump-range-label").textContent = "Typical for pools around " + pumpGuide.range + " — your pool is about " + fmt(gallons, 0) + " gallons.";

    // Filter table
    renderTierTable("pfh-filter-table", tier, function (t) {
      var g = M.FILTER_GUIDE[t];
      return [g.range, g.sand, g.cartridge];
    });

    // Heater tab's summary guide table (this section's own static
    // reference table — not the live heater calculator, which is its
    // own section and renders pfh-heater-table2 itself).
    var heaterTier = M.heaterSizeTierExport ? M.heaterSizeTierExport(gallons) : tierForHeater(gallons);
    renderTierTable("pfh-heater-guide-table", heaterTier, function (t) {
      var g = M.HEATER_SIZE_GUIDE[t];
      return [g.range, g.gas, g.heatpump];
    });

    // Plumbing check
    var guideline = M.plumbingGuidelineGpm();
    var checkEl = $("#pfh-pipe-check");
    var pipeLabelMap = { "1.5": "1.5″ plumbing", "2": "2″ plumbing", advanced: "your plumbing" };
    if (flow.gpm <= guideline) {
      checkEl.innerHTML = '<div class="pfh-check ok">' + iconCheck() + '<span><strong>Your required flow (' + fmt(flow.gpm, 0) + ' GPM)</strong> is within the general recommended guideline for ' + pipeLabelMap[state.pipeSize] + ' (~' + fmt(guideline, 0) + ' GPM).</span></div>';
    } else {
      checkEl.innerHTML = '<div class="pfh-check warn">' + iconWarn() + '<span><strong>Your required flow (' + fmt(flow.gpm, 0) + ' GPM)</strong> is above the general guideline for ' + pipeLabelMap[state.pipeSize] + ' (~' + fmt(guideline, 0) + ' GPM). Consider a larger pipe size or reviewing your turnover goal with a pro.</span></div>';
    }

    renderTabs();
  }

  /* ---- Section 3 : Pool Heater Calculator ---- */
  function renderHeaterSection() {
    var sizingPresent = M.sizingSectionPresent();
    var reusedBlock = $("#pfh-h-reused-block");
    var manualBlock = $("#pfh-h-manual-block");
    if (reusedBlock) reusedBlock.hidden = !sizingPresent;
    if (manualBlock) manualBlock.hidden = sizingPresent;

    if (!sizingPresent) {
      var areaInput = $("#pfh-h-manual-area");
      var gallonsInput = $("#pfh-h-manual-gallons");
      if (areaInput && document.activeElement !== areaInput) areaInput.value = state.heaterManualArea;
      if (gallonsInput && document.activeElement !== gallonsInput) gallonsInput.value = state.heaterManualGallons;
    }

    var hCurrent = $("#pfh-h-current"), hTarget = $("#pfh-h-target"), hAir = $("#pfh-h-air");
    if (document.activeElement !== hCurrent) hCurrent.value = state.currentTemp;
    if (document.activeElement !== hTarget) hTarget.value = state.targetTemp;
    if (document.activeElement !== hAir) hAir.value = state.airTemp;

    var h = M.calcHeater(); // internally reuses Section 2 when present, else the manual fields above
    $("#pfh-h-area-readout").textContent = fmt(h.area, 0);
    $("#pfh-h-gallons-readout").textContent = fmt(h.gallons, 0);
    $("#pfh-h-rise").textContent = fmt(h.rise, 0);
    $("#pfh-h-btu").textContent = fmt(Math.round(h.estimatedBtuPerHr / 1000) * 1000, 0);
    $("#pfh-h-total-btu").textContent = fmt(Math.round(h.totalBtuNeeded / 1000) * 1000, 0);
    $("#pfh-h-hours").textContent = h.rise > 0 ? (fmt(h.lowHours, 0) + "–" + fmt(h.highHours, 0)) : "0";

    var noteBody;
    if (state.heaterType === "gas") {
      noteBody = "This assumes a typical ~82% delivered efficiency for a gas/propane heater (rated input BTU vs. actual heat reaching the water). Climate, wind, humidity, pool exposure, a pool cover, and your desired recovery time all affect real-world heating speed.";
    } else {
      noteBody = "Heat pump output is not constant — it depends heavily on air temperature, humidity, and water temperature. At your entered air temperature we estimate reduced output versus the nameplate rating. Real performance varies by model and conditions.";
    }
    $("#pfh-h-note-body").textContent = noteBody;

    renderTierTable("pfh-heater-table2", h.tier, function (t) {
      var g = M.HEATER_SIZE_GUIDE[t];
      return [g.range, g.gas, g.heatpump];
    });
  }

  function tierForHeater(gallons) {
    if (gallons < 10000) return "small";
    if (gallons <= 20000) return "medium";
    return "large";
  }

  function renderTierTable(tableId, activeTier, rowFn) {
    var table = $("#" + tableId);
    if (!table) return;
    var tbody = table.querySelector("tbody");
    tbody.innerHTML = "";
    ["small", "medium", "large"].forEach(function (t) {
      var cells = rowFn(t);
      var tr = document.createElement("tr");
      if (t === activeTier) tr.className = "pfh-row-highlight";
      var badge = t === activeTier ? '<span class="pfh-table-badge">Your pool</span>' : "";
      tr.innerHTML = "<td>" + cells[0] + badge + "</td><td>" + cells[1] + "</td><td>" + cells[2] + "</td>";
      tbody.appendChild(tr);
    });
  }

  function iconCheck() {
    return '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7.25" stroke="currentColor" stroke-width="1.6"/><path d="M6.8 10.2 9 12.4l4.2-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  function iconWarn() {
    return '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.2 17.5 16h-15L10 3.2z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 8.4v3.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="10" cy="13.8" r="0.9" fill="currentColor"/></svg>';
  }

  /* ------------------------------------------------------------------ *
   * Init
   * ------------------------------------------------------------------ */
  function init() {
    // Up to 3 independent sections (Pump Runtime, Equipment Sizing, Pool
    // Heater) may each include this script on the same page. Wiring
    // (event listeners) and the first paint must only happen once no
    // matter how many times this file is evaluated.
    if (window.__mspPfhInited) return;
    window.__mspPfhInited = true;

    wireSegmented("data-shape-group", "shape");
    wireSegmented("data-tdh-group", "tdhLevel", "tdh", function () { $("#pfh-tdh-advanced-row").hidden = true; });
    wireSegmented("data-pipe-group", "pipeSize", "pipe");
    wireSegmented("data-heatertype-group", "heaterType", "heatertype");
    $all("[data-turnover-group]").forEach(function (group) {
      $all(".pfh-seg-btn", group).forEach(function (btn) {
        btn.addEventListener("click", function () {
          state.turnoverHours = parseFloat(btn.getAttribute("data-turnover"));
          renderAll();
        });
      });
    });
    wireShapeInputs();
    wireManualGallons();
    wireRuntimeGallons();
    wireSpeed();
    wireTdhAdvanced();
    wirePipeAdvanced();
    wireTabs();
    wireHeaterInputs();
    wireHeaterManualInputs();
    renderAll();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
